/**
 * Pack line scans (F7.3): only sacks of this run's batch that were issued to this run go in.
 * Pure functions of the projected site pack.
 */
import type { Grams } from '@/lib/weight'
import type { BagView, PackRunView, ProcessState } from '@/features/process/projection'
import { classifyCode, type Verdict } from '@/features/process/model/rules'

export function checkPackSack(state: ProcessState, run: PackRunView, code: string): Verdict<BagView> {
  const c = code.trim().toUpperCase()
  const k = classifyCode(state, c)
  if (k.kind === 'DEAD') return { ok: false, reason: k.status === 'VOID' ? 'TAG_VOID' : 'TAG_USED' }
  if (k.kind !== 'BAG') return { ok: false, reason: k.kind === 'UNKNOWN' ? 'TAG_UNKNOWN' : 'TAG_WRONG_KIND' }
  const bag = k.bag
  if (bag.tag_kind !== 'OUTPUT_BAG') return { ok: false, reason: 'NOT_A_SACK' }
  if (run.inputs.some((i) => i.container_id === bag.container_id)) return { ok: false, reason: 'DUPLICATE' }
  if (bag.status === 'CONSUMED' || state.usedTags.has(c)) return { ok: false, reason: 'TAG_USED' }
  if (bag.unit_id !== run.batch_unit_id) return { ok: false, reason: 'NOT_THIS_BATCH' }
  if (bag.hold !== 'NONE') return { ok: false, reason: 'LOT_ON_HOLD' }
  if (bag.issued_run_id !== run.id && !run.issued_container_ids.includes(bag.container_id))
    return { ok: false, reason: 'NOT_ISSUED' }
  return { ok: true, value: bag }
}

/** A typed sack net further than the margin from its machine net is typed twice (F7.3). */
export function sackNeedsSecondEntry(
  readG: Grams,
  tareG: Grams,
  machineNetG: Grams | null | undefined,
  marginG: Grams,
): boolean {
  if (machineNetG === null || machineNetG === undefined) return false
  return Math.abs(readG - tareG - machineNetG) > marginG
}
